/**
 * Lightweight first-party analytics tracker.
 * - Sends pageview on mount + on every route change
 * - Sends click events for any element with data-track="label"
 * - Persists a session id in localStorage so the backend can group hits
 * - Reads UTM params from the URL on landing and persists them for the session
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID_KEY = "mdm_sid";
const UTM_KEY = "mdm_utm";

function getOrMakeSid() {
  let sid = localStorage.getItem(SID_KEY);
  if (!sid) {
    sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SID_KEY, sid);
  }
  return sid;
}

function captureUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 200);
    });
    if (Object.keys(utm).length) {
      // Merge with previously captured UTM to keep the first-touch chain
      const prev = JSON.parse(localStorage.getItem(UTM_KEY) || "{}");
      localStorage.setItem(UTM_KEY, JSON.stringify({ ...prev, ...utm }));
    }
  } catch {
    /* silent */
  }
}

function readUtm() {
  try {
    return JSON.parse(localStorage.getItem(UTM_KEY) || "{}");
  } catch {
    return {};
  }
}

async function send(payload) {
  try {
    const utm = readUtm();
    const body = JSON.stringify({
      ...payload,
      ...utm,
      session_hint: getOrMakeSid(),
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/track`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${API}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* silent */
  }
}

export function trackPageview(path) {
  captureUtm();
  send({
    name: "pageview",
    path: path || window.location.pathname,
    referrer: document.referrer || "",
  });
}

export function trackClick(label, extra = {}) {
  send({
    name: "click",
    label,
    path: window.location.pathname,
    ...extra,
  });
}

/** Wire up automatic click capture: any element with data-track="some-label". */
let installed = false;
export function installAutoTracking() {
  if (installed) return;
  installed = true;
  document.addEventListener(
    "click",
    (e) => {
      const el = e.target.closest?.("[data-track]");
      if (!el) return;
      const label = el.getAttribute("data-track");
      if (label) trackClick(label);
    },
    true
  );
}
